import { useMemo } from 'react';
import { resolveVisibility } from './resolveVisibility';
import { UnfilteredConditionalArray } from './types';

/**
 * Filters an array of values based on whitelist and blacklist criteria
 * evaluated against a context object.
 *
 * This hook is useful for conditionally showing/hiding items in lists based on
 * dynamic context such as user roles, product types, market countries, broker
 * configurations, etc.
 *
 * @template ContextType - The type of the context object used for criteria evaluation
 * @template V - The type of values being filtered
 *
 * @param context - The context object to evaluate criteria against (e.g., { roles: ['admin'], marketCountry: 'SE' })
 * @param unfilteredValues - Array of objects containing values and optional filtering criteria
 * @param unfilteredValues[].value - The actual value to be returned if visible
 * @param unfilteredValues[].whitelist - Optional array of criteria. If provided, value is only visible if any criteria match
 * @param unfilteredValues[].blacklist - Optional array of criteria. If any match, value is hidden regardless of whitelist
 *
 * @returns Array of visible values (type V[])
 *
 * @example
 * // Filter menu items based on user roles and market
 * const context = {
 *   roles: ['admin', 'underwriter'],
 *   marketCountry: 'SE'
 * };
 *
 * const menuItems = useConditionalFilter(context, [
 *   {
 *     value: 'dashboard',
 *     // Always visible (no whitelist/blacklist)
 *   },
 *   {
 *     value: 'admin-panel',
 *     whitelist: [{ roles: ['admin'] }], // Only visible to admins
 *   },
 *   {
 *     value: 'reports',
 *     blacklist: [{ marketCountry: 'NO' }], // Hidden in Norway
 *   },
 *   {
 *     value: 'underwriting',
 *     whitelist: [{ roles: ['admin', 'underwriter'] }], // Visible to admins or underwriters
 *     blacklist: [{ marketCountry: ['FI', 'DK'] }], // But hidden in Finland and Denmark
 *   },
 * ]);
 * // Result: ['dashboard', 'admin-panel', 'reports', 'underwriting']
 *
 * Evaluation order per item:
 * 1. If context matches any blacklist criteria: item is filtered out
 * 2. If no whitelist is defined: item is included
 * 3. If context matches any whitelist criteria: item is included
 * 4. Otherwise: item is filtered out
 *
 * The hook is memoized and only recalculates when context or unfilteredValues change.
 */
export const useConditionalFilter = <ContextType extends object, V>(
  context: ContextType,
  unfilteredValues: UnfilteredConditionalArray<ContextType, V>,
): V[] => {
  return useMemo(
    () =>
      unfilteredValues
        .filter(
          (x) =>
            resolveVisibility({
              context,
              whitelist: x.whitelist,
              blacklist: x.blacklist,
            })[0],
        )
        .map((x) => x.value),
    [unfilteredValues, context],
  );
};
